import { Fragment, useState } from 'react';
import { Combobox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import './customSelect.css';

export default function CustomSelect({
  items,
  selected,
  setSelected,
  displayKey = 'name',
  idKey = 'id',
  placeholder = 'Suchen...',
  search = true,
  valueType = 'object',
  // NEU: für serverseitige Suche + Pagination
  searchTerm,
  onSearchChange,
  onLoadMore,
  hasMore = false,
  loading = false,
}) {
  const [internalQuery, setInternalQuery] = useState('');

  // Extern gesteuert, sobald onSearchChange übergeben wird — sonst wie bisher intern
  const isControlled = typeof onSearchChange === 'function';
  const query = isControlled ? (searchTerm ?? '') : internalQuery;

  const handleQueryChange = (event) => {
    const value = event.target.value;
    if (isControlled) {
      onSearchChange(value);
    } else {
      setInternalQuery(value);
    }
  };

  // Client-seitiges Filtern nur noch, wenn NICHT extern gesteuert
  const filteredItems = isControlled
    ? items
    : query === ''
      ? items
      : items.filter((item) =>
          item[displayKey]
            .toLowerCase()
            .replace(/\s+/g, '')
            .includes(query.toLowerCase().replace(/\s+/g, ''))
        );

  const handleSelection = (value) => {
    if (valueType === 'primitive') {
      let actualValue = value;
      if (value && typeof value === 'object') {
        actualValue = value[idKey] ?? value;
      }
      if (actualValue === 'true') actualValue = true;
      if (actualValue === 'false') actualValue = false;

      const matchedItem = items.find((item) => String(item[idKey]) === String(actualValue));
      if (matchedItem) {
        setSelected(matchedItem[idKey]);
      } else {
        setSelected(actualValue);
      }
      return;
    }
    setSelected(value);
  };

  // Nachladen, wenn beim Scrollen das untere Ende erreicht wird
  const handleOptionsScroll = (event) => {
    if (!onLoadMore || !hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = event.target;
    const nearBottom = scrollHeight - scrollTop - clientHeight < 40;
    if (nearBottom) {
      onLoadMore();
    }
  };

  if (!search) {
    return (
      <div className="select-container">
        <select
          className="native-select"
          value={valueType === 'primitive' ? (selected === undefined || selected === null ? '' : String(selected)) : (selected?.[idKey] ?? '')}
          onChange={(event) => {
            const selectedItem = items.find((item) => String(item[idKey]) === event.target.value);
            handleSelection(selectedItem || event.target.value);
          }}
        >
          <option value="">{placeholder}</option>
          {items.map((item) => (
            <option key={item[idKey]} value={valueType === 'primitive' ? String(item[idKey]) : item[idKey]}>
              {item[displayKey]}
            </option>
          ))}
        </select>
        <ChevronUpDownIcon className="native-select-icon" aria-hidden="true" />
      </div>
    );
  }

  return (
    <div className="select-container">
      <Combobox value={selected} onChange={handleSelection} by={idKey}>
        <div className="combobox-wrapper">
          <div className="input-area">
            <Combobox.Input
              className="combobox-input"
              displayValue={(item) =>
                item === null || item === undefined || item === ''
                  ? ''
                  : (typeof item === 'object' ? item[displayKey] : item)
              }
              onChange={handleQueryChange}
              placeholder={placeholder}
            />
            <Combobox.Button className="combobox-button">
              <ChevronUpDownIcon className="icon-vane" aria-hidden="true" />
            </Combobox.Button>
          </div>

          <Transition
            as={Fragment}
            leave="transition-fade"
            afterLeave={() => {
              if (!isControlled) setInternalQuery('');
            }}
          >
            <Combobox.Options className="options-panel" onScroll={handleOptionsScroll}>
              {filteredItems.length === 0 && query !== '' ? (
                <div className="no-results">Keine Ergebnisse gefunden.</div>
              ) : (
                filteredItems.map((item) => (
                  <Combobox.Option
                    key={item[idKey]}
                    className={({ active }) =>
                      `option-item ${active ? 'option-active' : 'option-inactive'}`
                    }
                    value={item}
                  >
                    {({ selected, active }) => (
                      <>
                        <span className={`option-text ${selected ? 'selected-bold' : ''}`}>
                          {item[displayKey]}
                        </span>
                        {selected && (
                          <span className={`check-container ${active ? 'check-active' : ''}`}>
                            <CheckIcon className="icon-check" aria-hidden="true" />
                          </span>
                        )}
                      </>
                    )}
                  </Combobox.Option>
                ))
              )}
              {loading && (
                <div className="no-results">Lädt weitere Kunden...</div>
              )}
            </Combobox.Options>
          </Transition>
        </div>
      </Combobox>
    </div>
  );
}